import React, { useState, useEffect, useRef } from 'react';
import ModuleTemplate from '../../ModuleTemplate';
import { useSpeakAnswer } from '../../lib/useSpeakAnswer';
import { fallbackWrongKey } from '../../lib/speechAnswerEvaluator';
import { useInstructionAudio } from '../../lib/useInstructionAudio';
import './MusicPerception.css';

/* eslint-disable react-hooks/exhaustive-deps */

const S3_BASE_URL = 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru/vocabulary';
const S3_AUDIO_URL = 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru/audio';

// Человекочитаемое название противопоставления (для отчёта: "громкая/тихая", а не 'loud_quiet')
const CONTRAST_LABELS = {
  loud_quiet: 'громкая/тихая',
  happy_sad: 'весёлая/грустная',
  fast_slow: 'быстрая/медленная',
};

const MUSIC_EXERCISES = [
  {
    id: 'loud_quiet',
    question: 'Это громкая или тихая музыка?',
    audioFile: 'loud_music.mp3', 
    correctKey: 'loud',
    options: [
      { key: 'loud', text: 'Громкая', img: 'loud_music' },
      { key: 'quiet', text: 'Тихая', img: 'quiet_music' }
    ]
  },
  {
    id: 'happy_sad',
    question: 'Это веселая или грустная музыка?',
    audioFile: 'joyful_music.mp3', 
    correctKey: 'happy',
    options: [
      { key: 'happy', text: 'Весёлая', img: 'happy_music' },
      { key: 'sad', text: 'Грустная', img: 'sad_music' }
    ]
  },
  {
    id: 'fast_slow',
    question: 'Это быстрая или медленная музыка?',
    audioFile: 'fast_music.mp3',
    correctKey: 'fast',
    options: [
      { key: 'fast', text: 'Быстрая', img: 'fast_music' },
      { key: 'slow', text: 'Медленная', img: 'slow_music' }
    ]
  }
];

const MusicPerception = ({ ageGroup, onBack, onComplete }) => {
  const speech = window.speechSynthesis || window.webkitSpeechSynthesis;
  const audioRef = useRef(new Audio());
  const { start: startSpeakRecognition } = useSpeakAnswer('MusicPerception');
  const { play: playInstruction, stop: stopInstruction } = useInstructionAudio();

  const [selectedMode, setSelectedMode] = useState(null);
  const [blinkTarget, setBlinkTarget] = useState('speak');
  
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  // Лог: какое противопоставление тестировали, что выбрал ребёнок — нужно для отчёта,
  // чтобы писать не только "2 из 3", а конкретно "не различает громкую/тихую музыку".
  const [answerLog, setAnswerLog] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [hasSpoken, setHasSpoken] = useState(false);
  const [recognizerReady, setRecognizerReady] = useState(false);

  const [highlightedImage, setHighlightedImage] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [clickedKey, setClickedKey] = useState(null);

  const questions = MUSIC_EXERCISES; 
  const currentQuestion = questions[currentQuestionIndex];
  const totalQuestions = questions.length;

  useEffect(() => {
    if (selectedMode) return;
    const interval = setInterval(() => {
      setBlinkTarget(prev => (prev === 'speak' ? 'show' : 'speak'));
    }, 700);
    return () => clearInterval(interval);
  }, [selectedMode]);

  useEffect(() => {
    return () => {
      audioRef.current.pause();
      speech.cancel();
      stopInstruction();
    };
  }, []);

  const playCurrentAudio = () => {
    if (!currentQuestion || hasSpoken) return;
    setHasSpoken(true);
    setIsCorrect(null);
    setClickedKey(null);
    setHighlightedImage(null);

    setIsListening(true);
    setIsSpeaking(false);
    setRecognizerReady(false);

    // В режиме "Говорить" распознаватель запускается одновременно с озвучкой
    // вопроса, а не только после её окончания — иначе словарь распознавания
    // подключался слишком поздно, и ребёнок успевал ответить раньше, чем
    // распознаватель был готов слушать.
    if (selectedMode === 'speak') {
      setIsProcessing(true);
      startSpeakRecognition(currentQuestion.options, {
        timeoutMs: 14000,
        onListening: () => setRecognizerReady(true),
        onResult: ({ matchedKey }) => {
          audioRef.current.pause();
          const answeredKey = matchedKey || fallbackWrongKey(currentQuestion.options, currentQuestion.correctKey);
          const isAnswerCorrect = answeredKey === currentQuestion.correctKey;
          setIsCorrect(isAnswerCorrect);
          recordAnswer(isAnswerCorrect);
          goToNextQuestion();
        },
      });
    }

    playInstruction(currentQuestion.question, () => {
      audioRef.current.src = `${S3_AUDIO_URL}/${currentQuestion.audioFile}`;
      audioRef.current.play().catch(e => console.error("Ошибка воспроизведения аудио:", e));

      // These clips run ~2 minutes — plenty to tell loud/quiet, happy/sad,
      // fast/slow apart, but far too long to make the child wait for the
      // whole thing before they can answer. Move on after a short preview
      // (or sooner if the clip is naturally short), same cutoff "show" mode
      // already gets via hasSpoken.
      let settled = false;
      const revealTurn = () => {
        if (settled) return;
        settled = true;
        setIsListening(false);
        setIsSpeaking(true);
      };
      audioRef.current.onended = revealTurn;
      setTimeout(revealTurn, 4000);
    });
  };

  useEffect(() => {
    if (selectedMode && currentQuestion && !showResult && !hasSpoken) {
      const timer = setTimeout(() => playCurrentAudio(), 500);
      return () => clearTimeout(timer);
    }
  }, [currentQuestionIndex, selectedMode, showResult]);

  const recordAnswer = (isCorrectResult) => {
    setAnswers(prev => [...prev, isCorrectResult]);
    setAnswerLog(prev => [...prev, { questionId: currentQuestion.id, correct: isCorrectResult, mode: selectedMode }]);
  };

  const goToNextQuestion = () => {
    audioRef.current.pause();
    setIsSpeaking(false);
    setHighlightedImage(null);
    setIsProcessing(false);
    setTimeout(() => {
      if (currentQuestionIndex < totalQuestions - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        setHasSpoken(false);
      } else {
        setShowResult(true);
      }
    }, 800);
  };

  const handleImageClick = (key) => {
    if (selectedMode !== 'show' || isProcessing || !hasSpoken) return;
    
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    speech.cancel();

    setIsProcessing(true);
    setIsListening(false); 
    setIsSpeaking(false);  
    setClickedKey(key);
    const correct = key === currentQuestion.correctKey;
    setIsCorrect(correct);
    recordAnswer(correct);
    setTimeout(() => goToNextQuestion(), 800);
  };

  useEffect(() => {
    if (showResult) {
      const timer = setTimeout(() => {
        const correctCount = answers.filter((a) => a === true).length;
        const score = Math.round((correctCount / totalQuestions) * 100);

        // Конкретные противопоставления, которые не различил — для отчёта
        const failedContrasts = [...new Set(
          answerLog.filter(a => !a.correct).map(a => CONTRAST_LABELS[a.questionId] || a.questionId)
        )];
        const okContrasts = [...new Set(
          answerLog.filter(a => a.correct).map(a => CONTRAST_LABELS[a.questionId] || a.questionId)
        )];
        const hasUnverifiedSpeakAnswers = answerLog.some(a => a.mode === 'speak');

        if (onComplete) {
          onComplete({
            testId: 'MusicPerception',
            score,
            details: { total: totalQuestions, correct: correctCount, failedContrasts, okContrasts, hasUnverifiedSpeakAnswers },
          });
        } else {
          onBack();
        }
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showResult]);

  if (showResult) {
    const correctCount = answers.filter((a) => a === true).length;
    return (
      <ModuleTemplate 
        progress={answers} 
        totalQuestions={totalQuestions} 
        forceStop={true}
        onComplete={onComplete || onBack}
      >
        <div className="result-content">
          <h2 className="result-title">🎉 Упражнение завершено!</h2>
          <div className="result-score">
            <span className="score-number">{correctCount}</span>
            <span className="score-total"> / {totalQuestions}</span>
          </div>
        </div>
      </ModuleTemplate>
    );
  }

  if (!currentQuestion) return (
    <ModuleTemplate 
      progress={answers} 
      totalQuestions={totalQuestions} 
      forceStop={true}
      onComplete={onComplete || onBack}
    >
      <div className="loading-text">Выберите режим ниже, чтобы начать</div>
    </ModuleTemplate>
  );

  return (
    <ModuleTemplate
      progress={answers}
      totalQuestions={totalQuestions}
      forceStop={true}
      onComplete={onComplete || onBack}
      disabled={!selectedMode}
    >
      <div className="musicperception-container">

        {!selectedMode && (
          <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', marginBottom: '30px' }}>
            <button
              onClick={() => setSelectedMode('speak')}
              style={{
                padding: '14px 28px', borderRadius: '50px',
                border: `2px solid ${blinkTarget === 'speak' ? '#7C3AED' : '#E5E0F5'}`,
                background: blinkTarget === 'speak' ? '#EDE9FE' : '#F8F6FF',
                color: '#1A1A2E', fontWeight: '600', cursor: 'pointer',
                fontFamily: 'Inter, sans-serif', fontSize: '18px', transition: 'all 0.3s',
                boxShadow: blinkTarget === 'speak' ? '0 4px 20px rgba(124, 58, 237, 0.35)' : '0 4px 15px rgba(0,0,0,0.05)'
              }}
            >
              🎙️ Говорить
            </button>
            <button
              onClick={() => setSelectedMode('show')}
              style={{
                padding: '14px 28px', borderRadius: '50px',
                border: `2px solid ${blinkTarget === 'show' ? '#7C3AED' : '#E5E0F5'}`,
                background: blinkTarget === 'show' ? '#EDE9FE' : '#F8F6FF',
                color: '#1A1A2E', fontWeight: '600', cursor: 'pointer',
                fontFamily: 'Inter, sans-serif', fontSize: '18px', transition: 'all 0.3s',
                boxShadow: blinkTarget === 'show' ? '0 4px 20px rgba(124, 58, 237, 0.35)' : '0 4px 15px rgba(0,0,0,0.05)'
              }}
            >
              👆 Показать
            </button>
          </div>
        )}

        <div className="musicperception-main-row">
          <div className="musicperception-images-grid">
            {currentQuestion.options.map((option) => {
              const isHighlighted = highlightedImage === option.key;
              const isAnswerCorrect = isCorrect === true && option.key === currentQuestion.correctKey;
              const isAnswerWrong = isCorrect === false && (selectedMode === 'show' ? option.key === clickedKey : option.key === currentQuestion.correctKey);
              
              return (
                <div 
                  key={option.key} 
                  className={`musicperception-image-wrapper ${isHighlighted ? 'highlighted' : ''} ${isAnswerCorrect ? 'answer-correct' : ''} ${isAnswerWrong ? 'answer-wrong' : ''}`}
                  onClick={() => handleImageClick(option.key)}
                  style={{ cursor: (selectedMode === 'show' && hasSpoken) ? 'pointer' : 'default' }}
                >
                  <img 
                    src={`${S3_BASE_URL}/${option.img}.png`} 
                    alt={option.text} 
                    className="musicperception-image" 
                    onError={(e) => { e.target.src = '/placeholder.png'; }} 
                  />
                </div>
              );
            })}    
          </div>
          
          <div className="musicperception-indicators">
            <div className={`indicator listen ${isListening ? 'active' : ''}`}><span className="indicator-icon">🎧</span></div>
            <div className={`indicator speak ${isSpeaking && (selectedMode !== 'speak' || recognizerReady) ? 'active' : ''}`}><span className="indicator-icon">🗣️</span></div>
          </div>
        </div>
      </div>
    </ModuleTemplate>
  );
};

export default MusicPerception;